// The dispatch family's payload accessors and shared subject builder — a LEAF
// module, deliberately: `dispatch.ts` composes `TERMINAL_RENDERERS_DISPATCH_ROLLOUT`
// (from `./dispatch-rollout`) into its own table, and `dispatch-rollout.ts` needs
// these same helpers, so they used to live IN `dispatch.ts` and `dispatch-rollout.ts`
// imported them from there — a cycle. Entering the module graph at
// `dispatch-rollout.ts` ran `dispatch.ts`'s body first (for the helpers), which hit
// the spread of `TERMINAL_RENDERERS_DISPATCH_ROLLOUT` before that binding
// initialized: `ReferenceError: Cannot access 'TERMINAL_RENDERERS_DISPATCH_ROLLOUT'
// before initialization`. Reproduced before this file existed; it is why
// `dispatch-rollout.ts` had no test file of its own — any test importing it directly
// crashed on import. This file imports nothing from either family module, so neither
// can cycle through it.

import {
  LlmKeyFailureEvidence,
  LlmKeyFailureEvidenceSchema,
} from '../../../value-objects/llm-credential';
import type { TerminalRendered } from '../../../wire/terminal';

// ─── narrow reads over an `unknown` payload ──────────────────────────────────

/** The payload as a plain object, or `undefined` — never throws. */
export function rec(v: unknown): Record<string, unknown> | undefined {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : undefined;
}

/** A field read as a string, or `undefined` if absent or the wrong shape. */
export function str(o: Record<string, unknown> | undefined, key: string): string | undefined {
  const v = o?.[key];
  return typeof v === 'string' ? v : undefined;
}

/** A field read as a finite number, or `undefined`. */
export function num(o: Record<string, unknown> | undefined, key: string): number | undefined {
  const v = o?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** `str`, falling back to a second field name — the two writer shapes disagree. */
export function strEither(
  o: Record<string, unknown> | undefined,
  a: string,
  b: string,
): string | undefined {
  return str(o, a) ?? str(o, b);
}

// ─── shared subject builders ──────────────────────────────────────────────────

/** Every `runner.*` kind's subject: the box. `null` when the row names none. */
export function boxSubject(runnerId: string | undefined) {
  if (runnerId === undefined) return null;
  return { kind: 'box' as const, id: runnerId, label: runnerId };
}

/**
 * The line a `dispatch.no_credential` row renders. Every reason but one is a dispatch
 * that sealed nothing; `route_pin_unserved` SEALED, on a key the lane's Agents-page
 * selection does not name, and "no credential" would be false of it.
 */
export function noCredentialLine(reasonKind: string, lane: string): TerminalRendered['line'] {
  return reasonKind === 'route_pin_unserved'
    ? { key: 'terminal.dispatch.selection_unserved', params: { lane } }
    : { key: 'terminal.dispatch.no_credential', params: { reason: reasonKind, lane } };
}

/**
 * The line a `dispatch.key_cooled` row renders: what the cool rests on (#6378). Every writer
 * (the `key_failed` ingest, the mid-run failover, the pool-key throttle) records the same
 * `KeyCoolAttribution` in `input` (`evidence`, `providerCode`).
 *
 * The recorded value is narrowed through `LlmKeyFailureEvidenceSchema`; one that does not
 * parse (absent, `null`, a value outside the vocabulary) renders the plain cool, never an
 * invented refusal. A parsed value is switched over EXHAUSTIVELY:
 *   - `provider_refused` — refused by the provider, with its own code when the row has one;
 *   - `transport_timeout` — the provider did not answer in time.
 * The `never` default makes a member added to `LlmKeyFailureEvidence` a compile error here,
 * so it gets its own line instead of silently rendering the plain cool.
 */
export function keyCooledLine(
  reason: string,
  evidence: string | undefined,
  code: string | undefined,
): TerminalRendered['line'] {
  const parsed = LlmKeyFailureEvidenceSchema.safeParse(evidence);
  if (!parsed.success) return { key: 'terminal.dispatch.key_cooled', params: { reason } };
  const recorded: LlmKeyFailureEvidence = parsed.data;
  switch (recorded) {
    case LlmKeyFailureEvidence.ProviderRefused:
      return code === undefined
        ? { key: 'terminal.dispatch.key_cooled_refused', params: { reason } }
        : { key: 'terminal.dispatch.key_cooled_refused_code', params: { reason, code } };
    case LlmKeyFailureEvidence.TransportTimeout:
      return { key: 'terminal.dispatch.key_cooled_timed_out', params: { reason } };
    default: {
      const unhandled: never = recorded;
      throw new Error(`keyCooledLine: unhandled LlmKeyFailureEvidence ${String(unhandled)}`);
    }
  }
}
